import React from 'react';

/**
 * Dashboard v2 KPI row — four metric tiles per `Dashboard v2.dc.html`
 * (label 12.5/500 secondary · value 24/600 · sub 11.5 muted · 1px border,
 * radius 10). Presentational: tiles arrive fully formatted; a `null` value
 * renders an honest em-dash — never a fabricated number.
 */
export interface KpiTile {
  label: string;
  /** Formatted headline number; `null` renders "—" (metric not available). */
  value: string | null;
  /** Small unit rendered inline after the value (mock: "days"). */
  unit?: string;
  /** Line under the value. */
  sub?: string;
  /** Value ink — mock renders "Awaiting your review" in cobalt. */
  tone?: 'ink' | 'action';
  /** Sub-line ink — mock renders the improving delta in success green. */
  subTone?: 'muted' | 'success';
}

export interface KpiRowProps {
  tiles: KpiTile[];
  className?: string;
}

export const KpiRow: React.FC<KpiRowProps> = ({ tiles, className = '' }) => (
  <div className={`grid grid-cols-2 gap-s5 md:grid-cols-4 ${className}`}>
    {tiles.map((tile) => (
      <div key={tile.label} className="rounded-card border border-line bg-surface px-[16px] py-s5">
        <div className="mb-[7px] text-[12.5px] font-medium text-ink-secondary">{tile.label}</div>
        <div
          className={`text-[24px] font-semibold leading-none ${
            tile.tone === 'action' ? 'text-action' : 'text-ink'
          }`}
        >
          {tile.value ?? '—'}
          {tile.value !== null && tile.unit && (
            <span className="text-[13px] font-medium text-ink-muted"> {tile.unit}</span>
          )}
        </div>
        {tile.sub && (
          <div
            className={`mt-[7px] text-meta ${
              tile.subTone === 'success' ? 'text-success-ink' : 'text-ink-muted'
            }`}
          >
            {tile.sub}
          </div>
        )}
      </div>
    ))}
  </div>
);

export default KpiRow;
